import Phaser from 'phaser';
import type { MainScene } from './MainScene';
import { loadSettings, saveSettings, GameSettings } from '../game/settings';
import { getItem } from '@neon-maw/shared';

export class UIScene extends Phaser.Scene {
  private mainScene!: MainScene;
  
  private hudBg!: Phaser.GameObjects.Graphics;
  private scoreText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private powerText!: Phaser.GameObjects.Text;
  
  private questTitle!: Phaser.GameObjects.Text;
  private questProgress!: Phaser.GameObjects.Text;
  
  private loadoutTexts: Phaser.GameObjects.Text[] = [];

  private settingsPanel!: Phaser.GameObjects.Container;
  private isSettingsOpen = false;
  private settings!: GameSettings;

  private tutorialContainer!: Phaser.GameObjects.Container;
  private tutorialText!: Phaser.GameObjects.Text;
  private lastDisplayedScore = -1;
  private wasQuestComplete = false;

  constructor() { super('ui'); }

  init(data: { main: MainScene }) {
    this.mainScene = data.main;
    this.settings = loadSettings();
    this.events.once('shutdown', this.cleanupDOM, this);
  }

  create() {
    const isLarge = this.settings.fontSize === 'large';
    const baseSize = isLarge ? 24 : 18;
    const smSize = isLarge ? 20 : 14;

    this.hudBg = this.add.graphics();
    this.hudBg.fillStyle(0x000000, 0.6);
    this.hudBg.fillRect(0, 0, this.scale.width, 80);

    // HUD Top left
    this.scoreText = this.add.text(20, 10, '', { fontFamily: 'monospace', fontSize: `${baseSize}px`, color: '#fff' });
    this.livesText = this.add.text(20, 35, '', { fontFamily: 'monospace', fontSize: `${baseSize}px`, color: '#fff' });
    this.powerText = this.add.text(200, 10, '', { fontFamily: 'monospace', fontSize: `${baseSize}px`, color: '#ffaa00' });

    // Quests Top middle
    const qx = this.scale.width / 2 - 100;
    this.questTitle = this.add.text(qx, 10, 'Quest', { fontFamily: 'monospace', fontSize: `${baseSize}px`, color: '#88ffee' });
    this.questProgress = this.add.text(qx, 35, '', { fontFamily: 'monospace', fontSize: `${smSize}px`, color: '#fff' });

    // Loadout Top right (before minimap)
    const lx = this.scale.width - 450;
    this.add.text(lx, 10, 'Loadout:', { fontFamily: 'monospace', fontSize: `${smSize}px`, color: '#fff' });
    for (let i = 0; i < 3; i++) {
      const t = this.add.text(lx, 30 + i * 20, '', { fontFamily: 'monospace', fontSize: `${smSize}px`, color: '#00ffff' });
      this.loadoutTexts.push(t);
    }

    // Settings Button
    // We keep the on-canvas button for mouse users
    const btnSet = this.add.text(20, this.scale.height - 40, '[ SETTINGS (ESC) ]', {
      fontFamily: 'monospace', fontSize: '16px', color: '#fff', backgroundColor: '#333'
    }).setInteractive({ useHandCursor: true });
    btnSet.on('pointerdown', () => this.toggleSettings());

    this.createSettingsPanel();

    this.tutorialContainer = this.add.container(this.scale.width / 2, this.scale.height - 100);
    const tutBg = this.add.rectangle(0, 0, 800, 60, 0x000044, 0.9);
    tutBg.setStrokeStyle(2, 0x00ffff);
    this.tutorialText = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '20px', color: '#fff', align: 'center', wordWrap: { width: 780 } }).setOrigin(0.5);
    this.tutorialContainer.add(tutBg);
    this.tutorialContainer.add(this.tutorialText);
    this.tutorialContainer.setVisible(false);
  }

  private domContainer!: HTMLDivElement;

  private createSettingsPanel() {
    // Create a DOM-based settings panel for native accessibility (tab-navigation, screen readers)
    this.domContainer = document.createElement('div');
    this.domContainer.id = 'a11y-settings-panel';
    this.domContainer.style.position = 'absolute';
    this.domContainer.style.top = '50%';
    this.domContainer.style.left = '50%';
    this.domContainer.style.transform = 'translate(-50%, -50%)';
    this.domContainer.style.width = '400px';
    this.domContainer.style.backgroundColor = '#222233f2';
    this.domContainer.style.border = '2px solid #fff';
    this.domContainer.style.color = '#fff';
    this.domContainer.style.fontFamily = 'monospace';
    this.domContainer.style.padding = '20px';
    this.domContainer.style.display = 'none';
    this.domContainer.style.flexDirection = 'column';
    this.domContainer.style.gap = '15px';
    this.domContainer.style.zIndex = '1000';
    this.domContainer.style.alignItems = 'flex-start';
    this.domContainer.innerHTML = `<h2 style="text-align:center; width:100%; margin-top:0;">SETTINGS & ACCESSIBILITY</h2>`;
    document.body.appendChild(this.domContainer);

    const makeCheckbox = (id: string, label: string, getValue: () => boolean, onToggle: (val: boolean) => void) => {
      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.gap = '10px';
      wrapper.style.alignItems = 'center';
      
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.id = id;
      input.checked = getValue();
      input.style.width = '20px';
      input.style.height = '20px';
      input.addEventListener('change', (e) => {
        onToggle((e.target as HTMLInputElement).checked);
        saveSettings(this.settings);
      });

      const lbl = document.createElement('label');
      lbl.htmlFor = id;
      lbl.innerText = label;
      lbl.style.fontSize = '18px';
      lbl.style.cursor = 'pointer';

      wrapper.appendChild(input);
      wrapper.appendChild(lbl);
      this.domContainer.appendChild(wrapper);
    };

    makeCheckbox('cb-high-contrast', 'High Contrast', () => this.settings.highContrast, (v) => {
      this.settings.highContrast = v;
      this.mainScene.applySettings(this.settings);
    });

    makeCheckbox('cb-reduce-motion', 'Reduce Motion', () => this.settings.reduceMotion, (v) => {
      this.settings.reduceMotion = v;
      this.mainScene.applySettings(this.settings);
    });

    makeCheckbox('cb-large-text', 'Large Text', () => this.settings.fontSize === 'large', (v) => {
      this.settings.fontSize = v ? 'large' : 'normal';
      this.scene.restart();
    });

    makeCheckbox('cb-colorblind', 'Colorblind Mode', () => this.settings.colorblindMode, (v) => {
      this.settings.colorblindMode = v;
      this.mainScene.applySettings(this.settings);
    });

    const closeBtn = document.createElement('button');
    closeBtn.innerText = '> CLOSE (ESC) <';
    closeBtn.id = 'btn-close-settings';
    closeBtn.style.marginTop = '20px';
    closeBtn.style.alignSelf = 'center';
    closeBtn.style.padding = '10px 20px';
    closeBtn.style.fontSize = '18px';
    closeBtn.style.backgroundColor = '#555';
    closeBtn.style.color = '#ff0';
    closeBtn.style.border = 'none';
    closeBtn.style.cursor = 'pointer';
    closeBtn.addEventListener('click', () => this.toggleSettings());
    this.domContainer.appendChild(closeBtn);

    // Allow closing with Escape while focus is inside the panel
    this.domContainer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.toggleSettings();
      }
    });
  }

  private toggleSettings() {
    this.isSettingsOpen = !this.isSettingsOpen;
    if (this.isSettingsOpen) {
      this.domContainer.style.display = 'flex';
      this.mainScene.scene.pause();
      const firstInput = this.domContainer.querySelector('input');
      if (firstInput) firstInput.focus();
    } else {
      this.domContainer.style.display = 'none';
      this.mainScene.scene.resume();
      // focus back to game
      window.focus();
    }
  }

  // Cleanup DOM
  cleanupDOM() {
    if (this.domContainer && this.domContainer.parentElement) {
      this.domContainer.parentElement.removeChild(this.domContainer);
    }
  }

  update() {
    const k = this.input.keyboard!;
    const pauseCode = this.settings.keyBindings.pause;
    const pauseKey = k.keys[Phaser.Input.Keyboard.KeyCodes[pauseCode as keyof typeof Phaser.Input.Keyboard.KeyCodes]] || k.addKey(pauseCode);
    
    if (Phaser.Input.Keyboard.JustDown(pauseKey)) {
        this.toggleSettings();
    }

    const state = this.mainScene.getState();
    if (!state) return;

        if (this.lastDisplayedScore !== state.score) {
      this.scoreText.setText(`SCORE: ${state.score}`);
      if (this.lastDisplayedScore !== -1 && !this.settings.reduceMotion) {
         this.tweens.add({
             targets: this.scoreText,
             scaleX: 1.5,
             scaleY: 1.5,
             yoyo: true,
             duration: 100,
             ease: 'Sine.easeInOut'
         });
         // Flash color
         this.scoreText.setTint(0xffff00);
         this.time.delayedCall(100, () => this.scoreText.clearTint());
      }
      this.lastDisplayedScore = state.score;
    }
    this.livesText.setText(`LIVES: ${state.lives}`);
    
    if (state.powerTicks > 0) {
      this.powerText.setText(`POWER: ${(state.powerTicks/60).toFixed(1)}s`);
      if (state.powerTicks < 180 && !this.settings.reduceMotion) { // under 3 seconds
         this.powerText.setAlpha((this.time.now % 400 < 200) ? 0.3 : 1);
         this.powerText.setColor('#ff0000');
      } else {
         this.powerText.setAlpha(1);
         this.powerText.setColor('#ffaa00');
      }
    } else if (state.shieldTicks > 0) {
      this.powerText.setText(`SHIELD: ${(state.shieldTicks/60).toFixed(1)}s`);
      this.powerText.setAlpha(1);
      this.powerText.setColor('#00ffff');
    } else {
      this.powerText.setText('');
      this.powerText.setAlpha(1);
    }

    if (state.quests && state.quests.length > 0) {
      const q = state.quests[0];
      this.questTitle.setText(`Quest: ${q.title}`);
      this.questProgress.setText(`Progress: ${q.progress} / ${q.target} ${q.complete ? '(DONE)' : ''}`);
      this.questProgress.setColor(q.complete ? '#00ff00' : '#ffffff');
      
      if (q.complete && !this.wasQuestComplete && !this.settings.reduceMotion) {
         this.tweens.add({
             targets: this.questTitle,
             scaleX: 1.2, scaleY: 1.2,
             yoyo: true, duration: 200, ease: 'Bounce.easeOut'
         });
         this.wasQuestComplete = true;
      } else if (!q.complete) {
         this.wasQuestComplete = false;
      }
    }

    // Loadout
    for (let i = 0; i < 3; i++) {
      if (i < state.inventory.length) {
        const item = state.inventory[i];
        this.loadoutTexts[i].setText(`- ${item.name}`);
      } else {
        this.loadoutTexts[i].setText('');
      }
    }

    if (state.biomeId === 'tutorial' || !state.biomeId) {
      this.tutorialContainer.setVisible(true);
      let msg = '';
      if (state.score === 0 && state.tick < 150) {
        msg = "Welcome to the Grid! Use Arrow Keys to move.";
      } else if (state.quests.length > 0 && !state.quests[0].complete && state.quests[0].id === 'tutorial_1') {
        msg = "Collect data shards (.) to gain points and progress your quest (top middle).";
      } else if (state.quests.length > 0 && !state.quests[0].complete && state.quests[0].id === 'tutorial_2') {
        if (state.powerTicks > 0) {
           msg = "Power Reversal! You can now delete Hunter programs (red) by touching them.";
        } else {
           msg = "Danger! Hunter programs will delete you. Grab a Power Surge (o) to reverse power.";
        }
      } else if (state.quests.length > 0 && !state.quests[0].complete && state.quests[0].id === 'tutorial_3') {
        msg = "Survive! Keep an eye on your loadout inventory (top right).";
      } else {
        msg = "Quest complete! Reach the Extraction point (pink square) to escape.";
      }
      this.tutorialText.setText(msg);
    } else {
      this.tutorialContainer.setVisible(false);
    }
  }
}
